import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { LocationUser, LocationUserAccess, LocationUserGrant, MerchantLocation, MerchantSummary } from '../../core/models';

// The "All locations" choice in an access row. A select cannot show null as a chosen value (it reads as
// nothing picked), and '*' can never collide with a location id (letters, digits, - _ . only).
const ALL_LOCATIONS = '*';
const MIN_PASSWORD_LENGTH = 10;

interface SelectOption { label: string; value: string; }
interface AccessRow { merchantId: string | null; locationId: string; }

const ALL_ONLY: SelectOption[] = [{ label: 'All locations', value: ALL_LOCATIONS }];

// Location users: people at a location who sign in with email + password (NOT Cognito — those are the
// sysadmins under Administration → Users & Roles). Each user's access is a list of grants: a merchant with
// all its locations (present and future), or one location of it.
//
// Shared by /location-users and the merchant page's Users tab. With a merchantId the list narrows to users
// holding any grant on that merchant, and a new user's first access row starts on it.
@Component({
    selector: 'app-location-users-panel',
    standalone: true,
    imports: [FormsModule, DatePipe, ButtonModule, TableModule, TagModule, MessageModule, DialogModule, InputTextModule, SelectModule, ToggleSwitchModule, TooltipModule],
    template: `
        <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
            <span class="text-sm text-surface-500 dark:text-surface-400">
                {{ merchantId() ? 'Users with access to this merchant or any of its locations.' : 'Every location user.' }}
            </span>
            <div class="flex gap-2">
                <p-button icon="pi pi-refresh" [text]="true" size="small" [loading]="loading()" (onClick)="reload()" />
                <p-button label="New user" icon="pi pi-plus" size="small" (onClick)="openCreate()" />
            </div>
        </div>

        @if (note()) {
            <p-message severity="success" styleClass="mb-3">{{ note() }}</p-message>
        }
        @if (error()) {
            <p-message severity="error" styleClass="mb-3">{{ error() }}</p-message>
        }

        <p-table [value]="users()" [loading]="loading()" [paginator]="users().length > 15" [rows]="15">
            <ng-template pTemplate="header">
                <tr>
                    <th>Email</th>
                    <th>Display name</th>
                    <th>Access</th>
                    <th>Status</th>
                    <th>Last sign-in</th>
                    <th style="width: 9rem"></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-u>
                <tr>
                    <td class="font-medium">{{ u.email }}</td>
                    <td>{{ u.displayName || '—' }}</td>
                    <td>
                        <div class="flex flex-wrap gap-1">
                            @for (a of u.access; track $index) {
                                <p-tag [value]="accessLabel(a)" [severity]="a.locationId ? 'secondary' : 'info'" />
                            }
                        </div>
                    </td>
                    <td>
                        <div class="flex flex-wrap gap-1">
                            <p-tag [value]="u.isActive ? 'Active' : 'Disabled'" [severity]="u.isActive ? 'success' : 'secondary'" />
                            @if (isLocked(u)) {
                                <p-tag value="Locked" severity="warn" [attr.title]="'Too many failed sign-ins — locked until ' + (u.lockedUntil | date: 'short') + '. Setting a password unlocks it.'" />
                            }
                        </div>
                    </td>
                    <td class="text-sm whitespace-nowrap">{{ u.lastSignInAt ? (u.lastSignInAt | date: 'short') : 'Never' }}</td>
                    <td>
                        <div class="flex justify-end gap-1">
                            <p-button icon="pi pi-pencil" [text]="true" size="small" ariaLabel="Edit" (onClick)="openEdit(u)" />
                            <p-button icon="pi pi-key" [text]="true" size="small" severity="secondary" ariaLabel="Set password" (onClick)="openPassword(u)" />
                            <p-button icon="pi pi-trash" [text]="true" size="small" severity="danger" ariaLabel="Delete" (onClick)="openDelete(u)" />
                        </div>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr><td colspan="6">{{ error() ? 'The users could not be read — the banner above says why.' : 'No location users yet.' }}</td></tr>
            </ng-template>
        </p-table>

        <!-- Create / edit -->
        <p-dialog [header]="editing() ? 'Edit location user' : 'New location user'" [(visible)]="userOpen" [modal]="true"
                  [style]="{ width: '42rem' }" [breakpoints]="{ '640px': '95vw' }">
            <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Email</label>
                    <input pInputText type="email" autocomplete="off" [(ngModel)]="form.email" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Display name</label>
                    <input pInputText autocomplete="off" [(ngModel)]="form.displayName" />
                </div>
                @if (editing()) {
                    <div class="flex items-center gap-2">
                        <p-toggleswitch [(ngModel)]="form.isActive" />
                        <span class="text-sm">Active — a disabled user cannot sign in</span>
                    </div>
                } @else {
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div class="flex flex-col gap-1">
                            <label class="font-medium">Password</label>
                            <input pInputText type="password" autocomplete="new-password" [(ngModel)]="form.password" />
                        </div>
                        <div class="flex flex-col gap-1">
                            <label class="font-medium">Confirm password</label>
                            <input pInputText type="password" autocomplete="new-password" [(ngModel)]="form.confirm" />
                        </div>
                    </div>
                    <small class="text-muted-color -mt-2">At least {{ minPasswordLength }} characters.</small>
                }

                <div class="flex flex-col gap-2">
                    <label class="font-medium">Access<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="'All locations' also covers any location the merchant adds later." tooltipPosition="top"></i></label>
                    @for (row of rows; track $index) {
                        <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
                            <p-select [options]="merchantOptions()" optionLabel="label" optionValue="value" [(ngModel)]="row.merchantId"
                                      (onChange)="onRowMerchantChange(row)" placeholder="Merchant" [filter]="true"
                                      appendTo="body" styleClass="w-full sm:w-64" />
                            <p-select [options]="locationOptionsFor(row.merchantId)" optionLabel="label" optionValue="value"
                                      [(ngModel)]="row.locationId" [disabled]="!row.merchantId" [filter]="true"
                                      appendTo="body" styleClass="w-full sm:flex-1" />
                            <p-button icon="pi pi-times" [text]="true" severity="secondary" ariaLabel="Remove access row"
                                      [disabled]="rows.length === 1" (onClick)="removeRow($index)" />
                        </div>
                    }
                    <div>
                        <p-button label="Add access" icon="pi pi-plus" size="small" [text]="true" (onClick)="addRow()" />
                    </div>
                </div>

                @if (formError()) { <p-message severity="error">{{ formError() }}</p-message> }
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="userOpen.set(false)" />
                <p-button [label]="editing() ? 'Save' : 'Create user'" icon="pi pi-check" [loading]="saving()" (onClick)="save()" />
            </ng-template>
        </p-dialog>

        <!-- Set password -->
        <p-dialog header="Set password" [(visible)]="passwordOpen" [modal]="true" [style]="{ width: '30rem' }" [breakpoints]="{ '640px': '95vw' }">
            <div class="flex flex-col gap-3">
                <p class="text-sm m-0">
                    New password for <b>{{ passwordTarget()?.email }}</b>. Setting it also clears a sign-in lock.
                </p>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Password</label>
                    <input pInputText type="password" autocomplete="new-password" [(ngModel)]="passwordForm.password" />
                    <small class="text-muted-color">At least {{ minPasswordLength }} characters.</small>
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Confirm password</label>
                    <input pInputText type="password" autocomplete="new-password" [(ngModel)]="passwordForm.confirm" />
                </div>
                @if (passwordError()) { <p-message severity="error">{{ passwordError() }}</p-message> }
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="passwordOpen.set(false)" />
                <p-button label="Set password" icon="pi pi-key" [loading]="saving()" (onClick)="savePassword()" />
            </ng-template>
        </p-dialog>

        <!-- Delete -->
        <p-dialog header="Delete location user" [(visible)]="deleteOpen" [modal]="true" [style]="{ width: '30rem' }" [breakpoints]="{ '640px': '95vw' }">
            <p class="text-sm">
                Delete <b>{{ deleteTarget()?.email }}</b>? They will no longer be able to sign in, and this cannot be undone.
            </p>
            @if (deleteError()) { <p-message severity="error">{{ deleteError() }}</p-message> }
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="deleteOpen.set(false)" />
                <p-button label="Delete" severity="danger" [loading]="saving()" (onClick)="confirmDelete()" />
            </ng-template>
        </p-dialog>
    `
})
export class LocationUsersPanel {
    private readonly api = inject(MiddlewareApi);

    /** Narrows the list to this merchant's users and starts a new user's access on it. */
    readonly merchantId = input<string | null>(null);

    readonly minPasswordLength = MIN_PASSWORD_LENGTH;

    readonly users = signal<LocationUser[]>([]);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly note = signal<string | null>(null);
    readonly saving = signal(false);
    private loadSub?: Subscription;

    // Access editor sources: every merchant, and each merchant's locations fetched the first time a row
    // picks it.
    readonly merchants = signal<MerchantSummary[]>([]);
    private merchantsRequested = false;
    private readonly locationsByMerchant = signal<Record<string, MerchantLocation[]>>({});
    private readonly locationsRequested = new Set<string>();
    // Inactive is marked on both selects: a grant on an inactive merchant is as inert as one on an inactive
    // location — sign-in drops inactive merchants entirely.
    readonly merchantOptions = computed<SelectOption[]>(() =>
        this.merchants().map((m) => ({ label: m.isActive ? m.name : `${m.name} (inactive)`, value: m.merchantId })));
    private readonly locationOptions = computed(() => {
        const out: Record<string, SelectOption[]> = {};
        for (const [merchantId, locations] of Object.entries(this.locationsByMerchant())) {
            out[merchantId] = [
                ...ALL_ONLY,
                ...locations.map((l) => ({ label: l.isActive ? l.name : `${l.name} (inactive)`, value: l.locationId }))
            ];
        }
        return out;
    });

    // Create / edit
    readonly userOpen = signal(false);
    readonly editing = signal<LocationUser | null>(null);
    readonly formError = signal<string | null>(null);
    form = { email: '', displayName: '', password: '', confirm: '', isActive: true };
    rows: AccessRow[] = [];

    // Set password
    readonly passwordOpen = signal(false);
    readonly passwordTarget = signal<LocationUser | null>(null);
    readonly passwordError = signal<string | null>(null);
    passwordForm = { password: '', confirm: '' };

    // Delete
    readonly deleteOpen = signal(false);
    readonly deleteTarget = signal<LocationUser | null>(null);
    readonly deleteError = signal<string | null>(null);

    constructor() {
        // First load, and again whenever the host changes the merchant.
        effect(() => {
            const merchantId = this.merchantId();
            untracked(() => this.load(merchantId));
        });
    }

    /** Re-read the users, and forget cached merchant locations — for hosts that just changed them. */
    reload(): void {
        this.locationsRequested.clear();
        this.locationsByMerchant.set({});
        this.load(this.merchantId());
    }

    accessLabel(a: LocationUserAccess): string {
        return `${a.merchantName} — ${a.locationId ? (a.locationName ?? a.locationId) : 'all locations'}`;
    }

    isLocked(u: LocationUser): boolean {
        return !!u.lockedUntil && new Date(u.lockedUntil).getTime() > Date.now();
    }

    locationOptionsFor(merchantId: string | null): SelectOption[] {
        return (merchantId && this.locationOptions()[merchantId]) || ALL_ONLY;
    }

    openCreate(): void {
        this.editing.set(null);
        this.form = { email: '', displayName: '', password: '', confirm: '', isActive: true };
        this.rows = [{ merchantId: this.merchantId(), locationId: ALL_LOCATIONS }];
        this.openUserDialog();
    }

    openEdit(u: LocationUser): void {
        this.editing.set(u);
        this.form = { email: u.email, displayName: u.displayName, password: '', confirm: '', isActive: u.isActive };
        this.rows = u.access.length
            ? u.access.map((a) => ({ merchantId: a.merchantId, locationId: a.locationId ?? ALL_LOCATIONS }))
            : [{ merchantId: this.merchantId(), locationId: ALL_LOCATIONS }];
        this.openUserDialog();
    }

    addRow(): void {
        this.rows = [...this.rows, { merchantId: this.merchantId(), locationId: ALL_LOCATIONS }];
        this.ensureLocations(this.merchantId());
    }

    removeRow(index: number): void {
        if (this.rows.length > 1) this.rows = this.rows.filter((_, i) => i !== index);
    }

    onRowMerchantChange(row: AccessRow): void {
        row.locationId = ALL_LOCATIONS;
        this.ensureLocations(row.merchantId);
    }

    save(): void {
        const email = this.form.email.trim();
        if (!email) { this.formError.set('Email is required.'); return; }
        const access = this.grants();
        if (!access) return;

        const editing = this.editing();
        if (!editing) {
            const passwordProblem = this.passwordProblem(this.form.password, this.form.confirm);
            if (passwordProblem) { this.formError.set(passwordProblem); return; }
        }

        this.saving.set(true);
        this.formError.set(null);
        const displayName = this.form.displayName.trim();
        const request = editing
            ? this.api.updateLocationUser(editing.userId, { email, displayName, isActive: this.form.isActive, access })
            : this.api.createLocationUser({ email, displayName, password: this.form.password, access });
        request.subscribe({
            next: (u) => {
                this.saving.set(false);
                this.userOpen.set(false);
                this.note.set(editing ? `Saved ${u.email}.` : `Created ${u.email} — they can sign in now.`);
                this.load(this.merchantId());
            },
            error: (e) => { this.saving.set(false); this.formError.set(this.describe(e, 'location-users:manage')); }
        });
    }

    openPassword(u: LocationUser): void {
        this.passwordTarget.set(u);
        this.passwordForm = { password: '', confirm: '' };
        this.passwordError.set(null);
        this.passwordOpen.set(true);
    }

    savePassword(): void {
        const target = this.passwordTarget();
        if (!target) return;
        const problem = this.passwordProblem(this.passwordForm.password, this.passwordForm.confirm);
        if (problem) { this.passwordError.set(problem); return; }
        this.saving.set(true);
        this.passwordError.set(null);
        this.api.setLocationUserPassword(target.userId, this.passwordForm.password).subscribe({
            next: () => {
                this.saving.set(false);
                this.passwordOpen.set(false);
                this.note.set(`Password set for ${target.email}.`);
                this.load(this.merchantId());
            },
            error: (e) => { this.saving.set(false); this.passwordError.set(this.describe(e, 'location-users:manage')); }
        });
    }

    openDelete(u: LocationUser): void {
        this.deleteTarget.set(u);
        this.deleteError.set(null);
        this.deleteOpen.set(true);
    }

    confirmDelete(): void {
        const target = this.deleteTarget();
        if (!target) return;
        this.saving.set(true);
        this.deleteError.set(null);
        this.api.deleteLocationUser(target.userId).subscribe({
            next: () => {
                this.saving.set(false);
                this.deleteOpen.set(false);
                this.note.set(`Deleted ${target.email}.`);
                this.load(this.merchantId());
            },
            error: (e) => { this.saving.set(false); this.deleteError.set(this.describe(e, 'location-users:manage')); }
        });
    }

    private load(merchantId: string | null): void {
        // A newer request (the host switched merchant) replaces one still in flight.
        this.loadSub?.unsubscribe();
        this.loading.set(true);
        this.error.set(null);
        this.loadSub = this.api.getLocationUsers(merchantId).subscribe({
            next: (u) => { this.users.set(u ?? []); this.loading.set(false); },
            // The rows on screen are no longer known to be true — they would otherwise outlive a deleted user
            // under its own "Deleted …" banner, or follow a merchant switch the request never completed.
            error: (e) => {
                this.users.set([]);
                this.error.set(this.describe(e, 'location-users:manage'));
                this.loading.set(false);
            }
        });
    }

    private openUserDialog(): void {
        this.formError.set(null);
        this.note.set(null);
        this.loadMerchants();
        for (const row of this.rows) this.ensureLocations(row.merchantId);
        this.userOpen.set(true);
    }

    private loadMerchants(): void {
        if (this.merchantsRequested) return;
        this.merchantsRequested = true;
        this.api.getMerchants().subscribe({
            next: (m) => this.merchants.set(m ?? []),
            error: (e) => { this.merchantsRequested = false; this.formError.set(this.describe(e, 'merchants:read')); }
        });
    }

    private ensureLocations(merchantId: string | null): void {
        if (!merchantId || this.locationsRequested.has(merchantId)) return;
        this.locationsRequested.add(merchantId);
        this.api.getMerchant(merchantId).subscribe({
            next: (d) => this.locationsByMerchant.update((all) => ({ ...all, [merchantId]: d.locations ?? [] })),
            error: (e) => { this.locationsRequested.delete(merchantId); this.formError.set(this.describe(e, 'merchants:read')); }
        });
    }

    /** The rows as grants, with exact duplicates dropped. Null (and the reason shown) when a row is incomplete. */
    private grants(): LocationUserGrant[] | null {
        if (!this.rows.length) { this.formError.set('Give the user access to at least one merchant.'); return null; }
        const grants = new Map<string, LocationUserGrant>();
        for (const row of this.rows) {
            if (!row.merchantId) { this.formError.set('Choose a merchant on every access row.'); return null; }
            const locationId = row.locationId === ALL_LOCATIONS ? null : row.locationId;
            grants.set(`${row.merchantId}|${locationId ?? ''}`, { merchantId: row.merchantId, locationId });
        }
        return [...grants.values()];
    }

    private passwordProblem(password: string, confirm: string): string | null {
        if (password.length < MIN_PASSWORD_LENGTH) return `The password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
        if (password !== confirm) return 'The passwords do not match.';
        return null;
    }

    private describe(e: any, permission: string): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        // 400/404/409 carry { error }: a sentence written for the operator.
        if (e?.error?.error) return e.error.error;
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return `Forbidden — your role lacks the ${permission} permission.`;
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
